import {ServiceStatus} from 'crqlar-react-common/dist/models/service/serviceStatus.model';
import {useAllSectors} from 'crqlar-react-common/dist/utils/hooks/sectors/useSectors';
import {useAllServices} from 'crqlar-react-common/dist/utils/hooks/services/useServices';
import {useTranslation} from 'react-i18next';
import {useIsFetching} from 'react-query';

import {
    IdFilterSelection,
    RESERVATION_SOURCE_ORDER,
    readSectorFilter,
    readServiceFilter,
    readSources,
    reservationSourceLabel,
    writeSectorFilter,
    writeServiceFilter
} from '../../../config/reservationTriggers';
import {MultiSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';
import SectorMultiSelectField from './SectorMultiSelectField';

// A draft service is never on a reservation. No stored status = ACTIVE, as in `utils/serviceSave.util.ts`.
const OFFERED_SERVICE_STATUSES: ServiceStatus[] = [
    ServiceStatus.ACTIVE,
    ServiceStatus.ACTIVE_NOT_BOOKABLE
];

const isOfferedService = (status: ServiceStatus | undefined): boolean =>
    OFFERED_SERVICE_STATUSES.includes(status ?? ServiceStatus.ACTIVE);

interface ReservationTriggerFiltersProps {
    nodeConfig: Record<string, any>;
    setNodeConfig: (config: Record<string, any>) => void;
    readOnly?: boolean;
}

/**
 * The filters both reservation triggers share. No stored `sources` = all (nothing written until a change);
 * `sources: []` never fires. Sector/service ids go through the `config/reservationTriggers` helpers.
 */
const ReservationTriggerFilters = ({nodeConfig, setNodeConfig, readOnly}: ReservationTriggerFiltersProps) => {
    const {t} = useTranslation();
    const sectors = useAllSectors();
    const services = useAllServices();
    // The hook returns only the list, so the in-flight state comes from the query cache.
    const servicesLoading = useIsFetching(['allServices']) > 0;

    const sectorFilter = readSectorFilter(nodeConfig);
    const serviceFilter = readServiceFilter(nodeConfig);
    const sources = readSources(nodeConfig) ?? RESERVATION_SOURCE_ORDER;

    const sourceOptions: SelectOption[] = RESERVATION_SOURCE_ORDER.map(source => ({
        value: source,
        label: reservationSourceLabel(source, t)
    }));

    // Only services in the picked sectors: any other pair of filters can never both match.
    const belongsToSelectedSectors = (sectorIds: string[] | undefined): boolean =>
        !sectorFilter.ids.length || !!sectorIds?.some(sectorId => sectorFilter.ids.includes(sectorId));

    // The select keeps the order it is handed: sectors A to Z, services A to Z inside them.
    const serviceOptions: SelectOption[] = services
        .filter(service => isOfferedService(service.status))
        .filter(service => belongsToSelectedSectors(service.sectorIds))
        .map(service => ({
            value: service.id!,
            label: service.name || '',
            group: sectors.find(sector => sector.id === service.sectorIds?.[0])?.name || t('Other')
        }))
        .sort((left, right) => left.group.localeCompare(right.group) || left.label.localeCompare(right.label));

    const serviceName = (serviceId: string): string | undefined =>
        services.find(service => service.id === serviceId)?.name || serviceFilter.names[serviceId];

    const handleSectorsChange = (selection: IdFilterSelection[]) => {
        const nextSectorIds = selection.map(entry => entry.id);

        // Services whose sectors left the filter go too; one the catalogue lacks stays (its sectors are unknown).
        const keptServiceIds = serviceFilter.ids.filter(serviceId => {
            if (!nextSectorIds.length) return true;

            const service = services.find(candidate => candidate.id === serviceId);
            if (!service) return true;

            return !!service.sectorIds?.some(sectorId => nextSectorIds.includes(sectorId));
        });

        setNodeConfig({
            ...nodeConfig,
            ...writeSectorFilter(selection),
            ...(keptServiceIds.length === serviceFilter.ids.length
                ? {}
                : writeServiceFilter(keptServiceIds.map(id => ({id, name: serviceName(id)}))))
        });
    };

    return (
        <>
            <MultiSelect
                label={t('Only when created in')}
                placeholder={t('Pick at least one source')}
                value={sources}
                options={sourceOptions}
                onChange={values => setNodeConfig({...nodeConfig, sources: values})}
                disabled={readOnly}
            />

            <SectorMultiSelectField
                label={t('Only in sector')}
                filter={sectorFilter}
                onChange={handleSectorsChange}
                disabled={readOnly}
            />

            {/* `loading` stays undefined once settled: only a non-empty list proves a stored service is gone. */}
            <MultiSelect
                label={t('Only for service')}
                placeholder={t('Any service')}
                value={serviceFilter.ids}
                options={serviceOptions}
                unknownLabels={serviceFilter.names}
                loading={servicesLoading || undefined}
                onChange={ids => setNodeConfig({
                    ...nodeConfig,
                    ...writeServiceFilter(ids.map(id => ({id, name: serviceName(id)})))
                })}
                disabled={readOnly}
            />
        </>
    );
};

export default ReservationTriggerFilters;
